import { Box, useTheme } from "@mui/material";
import {
    useEffect,
    useRef
} from "react";
import { OrgChart } from "d3-org-chart";
import * as d3 from 'd3'

interface OrganizationChartProps {
    data: {
        parentId: string | number | null;
        id: string | number;
        name: string;
        title: string;
    }[]
}

export default function OrganizationChart({ data }: OrganizationChartProps): JSX.Element {
    const theme = useTheme()

    const maxNameLength = data.reduce((max, curr) => curr.name.length > max ? curr.name.length : max, 0)

    const d3Container = useRef(null);

    let chart: any = null;

    const colorMap: { [key: string]: string | undefined } = {
        "rwm": theme.palette.grey[700],
        "rtl": theme.palette.success[800],
        "rsm": theme.palette.warning[500],
        "rex": theme.palette.primary[500],
        "ia" : theme.palette.customColors.brownishRed,
    }

    useEffect(() => {
        if (data && d3Container.current) {
            if (!chart) {
                chart = new OrgChart();
            }

            chart
                .container(d3Container.current)
                .data(data)
                .nodeHeight(() => maxNameLength + 65 > 110 ? maxNameLength + 65 : 110)
                .nodeWidth(() => 220)
                .childrenMargin(() => 50)
                .compactMarginBetween(() => 25)
                .compactMarginPair(() => 50)
                .neighbourMargin(() => 25)
                .siblingsMargin(() => 25)
                .nodeContent(function (d: any) {
                    return `
                    <div 
                        style="
                            width:${d.width}px;
                            height:${d.height}px;
                            background-color:${colorMap[(d?.data?.title ?? "")?.toLowerCase()]};
                            padding: 10px;
                            display: ${d.data.name == "ROOT" ? "none" : "flex"};
                            flex-direction: column;
                            row-gap: 8px;
                            justify-content: center;
                            align-items: center;
                            "
                        >
                        <div 
                            style="
                                color:#ffffff;
                                font-weight:bold;
                                font-size:16px;
                                text-align:center;
                                "
                        >
                            ${d?.data?.name ?? ""} 
                        </div>
                        <div 
                            style="
                                text-align:center;
                                color:#ffffff;
                                font-size:14px";
                                font-weight:400;
                        >
                            (${d?.data?.title ?? ""}) 
                        </div>
                   </div>
      `;
                })
                .nodeUpdate(function (this: any, d: any) {
                    d3.select(this)
                        .select('.node-rect')
                        .attr("stroke", (d: any) => d.data._highlighted || d.data._upToTheRootHighlighted ? theme.palette.grey[100] : 'none')
                        .attr("stroke-width", d.data._highlighted || d.data._upToTheRootHighlighted ? 10 : 1)

                    d3.select(this)
                        .attr('class', 'node')
                        .style('visibility', (d: any) => d.parent ? 'visible' : 'hidden')
                })
                .linkUpdate(function (this: any, d: any) {
                    d3.select(this)
                        .attr("stroke", (d: any) => d.data._upToTheRootHighlighted ? theme.palette.grey[100] : theme.palette.grey[500])
                        .attr("stroke-width", (d: any) => d.data._upToTheRootHighlighted ? 5 : 2)
                        .style('visibility', (d: any) => d.depth > 1 ? 'visible' : 'hidden')

                    if (d.data._upToTheRootHighlighted) {
                        d3.select(this).raise()
                    }
                })
                .compact(false)
                .render()
        }
    }, [data, d3Container.current]);

    return (
        <Box ref={d3Container} />
    );
};